import { useCallback, useEffect, useState } from 'react';
import { apiGet } from '../api/client';

// Stage 38: Yesterday tasks treated as projects. Mirrors the
// useProjects hook's shape so ProjectsView can swap between the two
// lists with minimal branching.

export interface YesterdayProjectBoundPanel {
  id: string;
  koji: string | null;
  displayLabel: string | null;
}

export interface YesterdayProjectSummary {
  id: string;
  name: string;
  koji: string;
  scheduleSummary: string;
  status: string;
  lastRun: number | null;
  lastResult: string | null;
  occurrencesUsed: number;
  maxOccurrences: number;
  selfRenew: boolean;
  enableTools: boolean;
  runCount: number;
  boundPanel: YesterdayProjectBoundPanel | null;
}

export interface YesterdayProjectRun {
  key: string;
  basename: string;
  ext: 'md' | 'html' | 'other';
  uploaded: string;
  size: number;
  signedUrl: string;
}

export interface YesterdayProjectTask {
  id: string;
  name: string;
  prompt: string;
  koji: string;
  scheduleType: string;
  scheduleTime: string;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  scheduleSummary: string;
  status: string;
  lastRun: number | null;
  lastResult: string | null;
  lastError: string | null;
  nextRun: number;
  occurrencesUsed: number;
  maxOccurrences: number;
  selfRenew: boolean;
  enableTools: boolean;
  createdAt: number;
}

export interface YesterdayProjectDetail {
  task: YesterdayProjectTask;
  boundPanel: YesterdayProjectBoundPanel | null;
  runs: YesterdayProjectRun[];
}

export interface UseYesterdayProjectsResult {
  list: YesterdayProjectSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  openDetail: (id: string) => Promise<YesterdayProjectDetail | null>;
}

export function useYesterdayProjects(): UseYesterdayProjectsResult {
  const [list, setList] = useState<YesterdayProjectSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiGet<{ projects: YesterdayProjectSummary[] }>(
        '/api/yesterday-projects',
      );
      setList(r.projects);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const openDetail = useCallback(
    async (id: string): Promise<YesterdayProjectDetail | null> => {
      try {
        return await apiGet<YesterdayProjectDetail>(
          `/api/yesterday-projects/${encodeURIComponent(id)}`,
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      }
    },
    [],
  );

  return { list, loading, error, refresh, openDetail };
}
